import apiClient from 'src/api/axiosClient';
import { ApiResponse } from 'src/api/axiosClient/types';
import { normalizeContactCaptureSettings, toContactCaptureApiSettings } from '../helpers';
import {
  ContactCapture,
  ContactCaptureListParams,
  ContactCaptureListResponse,
  ContactCaptureSettings,
  ContactCaptureStatus,
  ContactCaptureType,
  ContactCaptureUpsertData,
} from '../types';

type ContactCaptureApiType = 'popup' | 'form' | 'landing_page';

type ContactCaptureApiView = {
  id: string;
  companyId: string;
  name: string;
  type: ContactCaptureApiType;
  campaignId: string | null;
  settings: Record<string, unknown>;
  publicId: string;
  status: ContactCaptureStatus | 'deleted';
  createdBy: string | null;
  updatedBy: string | null;
  dateCreated: string;
  dateUpdated: string;
  viewCount: number;
  captureCount: number;
  hasUnpublishedChanges: boolean;
  consentText?: string;
  allowedOrigins?: string[];
};

type ContactCaptureApiUpsertBody = {
  allowedOrigins: string[];
  name: string;
  type: ContactCaptureApiType;
  campaignId: string;
  settings: Record<string, unknown>;
};

type ContactCaptureApiListResponse = {
  items: ContactCaptureApiView[];
  count: number;
};

const TYPE_TO_API: Record<ContactCaptureType, ContactCaptureApiType> = {
  popup: 'popup',
  inline: 'form',
};

const TYPE_FROM_API = Object.fromEntries(
  Object.entries(TYPE_TO_API).map(([type, apiType]) => [apiType, type]),
) as Record<ContactCaptureApiType, ContactCaptureType>;

const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  type: 'type',
  status: 'status',
  views: 'view_count',
  contacts: 'capture_count',
  createdAt: 'date_created',
  updatedAt: 'date_updated',
};

export const getContactCaptureServerSort = (sort?: string): string | undefined => {
  if (!sort) return undefined;
  const match = sort.match(/^(.*)_(asc|desc)$/);
  if (!match) return undefined;
  const field = SORT_FIELDS[match[1]];
  return field ? `${field}_${match[2]}` : undefined;
};

type SupportedApiView = ContactCaptureApiView & {
  status: ContactCaptureStatus;
  type: Exclude<ContactCaptureApiType, 'landing_page'>;
};

/** Landing pages were retired; legacy records still exist in the backend but are not shown. */
const isSupported = (view: ContactCaptureApiView): view is SupportedApiView =>
  view.status !== 'deleted' && view.type !== 'landing_page';

const toContactCapture = (view: ContactCaptureApiView): ContactCapture => {
  if (!isSupported(view)) {
    throw new Error('This contact capture is no longer supported.');
  }

  return {
    id: view.id,
    companyId: view.companyId,
    publicId: view.publicId,
    allowedOrigins: view.allowedOrigins,
    name: view.name,
    type: TYPE_FROM_API[view.type],
    status: view.status,
    campaignId: view.campaignId,
    campaign: null,
    settings: normalizeContactCaptureSettings(view.settings as ContactCaptureSettings),
    views: view.viewCount,
    contacts: view.captureCount,
    consentText: view.consentText || '',
    createdAt: view.dateCreated,
    updatedAt: view.dateUpdated,
    hasUnpublishedChanges: view.hasUnpublishedChanges,
  };
};

const toApiBody = (data: ContactCaptureUpsertData): ContactCaptureApiUpsertBody => {
  if (!data.campaignId?.trim()) throw new Error('Choose an active join campaign.');
  return {
    allowedOrigins: data.allowedOrigins,
    name: data.name,
    type: TYPE_TO_API[data.type],
    campaignId: data.campaignId,
    settings: toContactCaptureApiSettings(data.settings),
  };
};

const mapResponse = <TApi, TDomain>(
  response: ApiResponse<TApi>,
  mapper: (data: TApi) => TDomain,
): ApiResponse<TDomain> => ({ ...response, data: mapper(response.data) });

/** API client matching the OpenAPI contact-captures contract. */
export class ContactCaptureApi {
  async list(params: ContactCaptureListParams): Promise<ApiResponse<ContactCaptureListResponse>> {
    const response = await apiClient.get<ContactCaptureApiListResponse>('/contact-captures', {
      params: {
        companyId: params.companyId,
        query: params.query,
        type: params.type ? TYPE_TO_API[params.type] : 'popup,form',
        status: params.status,
        sort: getContactCaptureServerSort(params.sort),
        offset: params.offset,
        limit: params.limit,
      },
    });

    return mapResponse(response, data => {
      const items = data.items.filter(isSupported);
      return {
        items: items.map(toContactCapture),
        count: data.count,
      };
    });
  }

  async read(companyId: string, id: string): Promise<ApiResponse<ContactCapture>> {
    const response = await apiClient.get<ContactCaptureApiView>(`/contact-captures/${id}`, {
      params: { companyId },
    });
    return mapResponse(response, toContactCapture);
  }

  async create(data: ContactCaptureUpsertData): Promise<ApiResponse<ContactCapture>> {
    const response = await apiClient.post<ContactCaptureApiView>(
      '/contact-captures',
      toApiBody(data),
      { params: { companyId: data.companyId } },
    );
    return mapResponse(response, toContactCapture);
  }

  async update(companyId: string, id: string, data: ContactCaptureUpsertData): Promise<ApiResponse<ContactCapture>> {
    const response = await apiClient.put<ContactCaptureApiView>(
      `/contact-captures/${id}`,
      toApiBody(data),
      { params: { companyId } },
    );
    return mapResponse(response, toContactCapture);
  }

  activate(companyId: string, id: string): Promise<ApiResponse<void>> {
    return apiClient.put<void>(`/contact-captures/${id}/activate`, null, { params: { companyId } });
  }

  deactivate(companyId: string, id: string): Promise<ApiResponse<void>> {
    return apiClient.put<void>(`/contact-captures/${id}/deactivate`, null, { params: { companyId } });
  }

  publish(companyId: string, id: string): Promise<ApiResponse<void>> {
    return apiClient.post<void>(`/contact-captures/${id}/publish`, null, { params: { companyId } });
  }

  duplicate(
    companyId: string,
    source: ContactCapture,
    campaignId: string,
    allowedOrigins: string[],
  ): Promise<ApiResponse<ContactCapture>> {
    return this.create({
      companyId,
      name: `${source.name} copy`,
      type: source.type,
      campaignId,
      settings: source.settings,
      allowedOrigins,
    });
  }

  delete(companyId: string, id: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/contact-captures/${id}`, { params: { companyId } });
  }
}

export const contactCaptureApi = new ContactCaptureApi();
